import { ListChecks } from "@phosphor-icons/react";
import BgGoo from "./api-playground/bg-goo";

type ModelPurpose =
  | "custom-hardware"
  | "fine-tune-image"
  | "pipeline"
  | "other";

export default function ModelVersionOnboarding({
  fullName,
  dockerImageName,
  intendedPurpose,
  showEditor,
}: {
  fullName: string;
  dockerImageName: string;
  intendedPurpose: ModelPurpose;
  showEditor: boolean;
}) {
  return (
    <div className="flex border border-r8-gray-12 my-lh relative">
      <div className="relative flex items-center justify-center w-1/2 min-w-16 overflow-hidden bg-gradient-to-t from-branding-red to-branding-pink border-r border-r8-gray-12">
        <div className="absolute inset-0 opacity-50">
          <BgGoo speed={0.8} resolution={2} depth={4} />
        </div>
        <ListChecks
          size={64}
          weight="thin"
          className="z-[1] fill-white size-12 sm:size-16"
        />
      </div>
      <div className="p-lh max-w-xl">
        <h2 className="font-heading text-r8-xl sm:text-r8-2xl pb-3">
          <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-br from-branding-red to-branding-pink">
            Your model is ready.
          </span>{" "}
          <span className="text-r8-gray-11">Create your masterpiece.</span>
        </h2>

        {intendedPurpose === "custom-hardware" && (
          <>
            <p className="mb-lh text-pretty">
              Check out the{" "}
              <a href="https://replicate.com/docs/guides/push-a-model">
                guide to pushing your own model with Cog
              </a>{" "}
              for next steps, then run these commands to push it to Replicate:
            </p>

            <pre className="code border whitespace-pre-wrap [word-break:break-all]">
              <code>
                {`cog login
cog push ${dockerImageName}`}
              </code>
            </pre>
          </>
        )}

        {intendedPurpose === "fine-tune-image" && (
          <>
            <p className="mb-05lh text-pretty">
              Now it's time to gather your training images, put them in a zip
              file, and kick off the training process.
            </p>

            <ul className="list-disc list-inside space-y-2">
              <li>
                <a href="https://replicate.com/replicate/fast-flux-trainer">
                  <span className="font-semibold">Get started:</span> Flux fine
                  tuner
                </a>
              </li>
              <li>
                <a href="https://replicate.com/docs/guides/fine-tune-an-image-model">
                  <span className="font-semibold">Guide:</span> Fine-tune an
                  image model
                </a>
              </li>
            </ul>
          </>
        )}

        {intendedPurpose === "pipeline" && (
          <>
            <p className="mb-05lh">
              You can start editing your model right in Replicate, with an AI
              agent on hand to help.
            </p>

            <ul className="list-disc list-inside space-y-2">
              <li>
                <a href={`/editor/${fullName}`}>
                  <span className="font-semibold">Editor:</span> Get started in
                  the editor
                </a>
              </li>
            </ul>
          </>
        )}

        {intendedPurpose === "other" && (
          <>
            <p className="mb-lh text-pretty">
              There are a few different ways to push models to Replicate. Choose
              a guide to get started:
            </p>

            <ul className="list-disc list-inside space-y-2">
              {showEditor ? (
                <li>
                  <a href={`/editor/${fullName}`}>
                    <span className="font-semibold">Editor:</span> Get started
                    in the editor
                  </a>
                </li>
              ) : null}
              <li>
                <a href="https://replicate.com/docs/guides/push-a-model">
                  <span className="font-semibold">Guide:</span> Push your own
                  model with Cog
                </a>
              </li>
              <li>
                <a href="https://replicate.com/docs/guides/fine-tune-an-image-model">
                  <span className="font-semibold">Guide:</span> Fine-tune an
                  image model (e.g. SDXL)
                </a>
              </li>
              <li>
                <a href="https://replicate.com/docs/guides/deploy-a-custom-model">
                  <span className="font-semibold">Guide:</span> Deploy a custom
                  model
                </a>
              </li>
            </ul>
          </>
        )}

        <p className="mt-4 mb-0 text-r8-gray-12">
          Or you can <a href="https://replicate.com/docs">read the docs</a>.
        </p>
      </div>
    </div>
  );
}
